import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { styled } from "@pigment-css/react";
import { usePopover } from "./usePopover";
import {
  CheckMark,
  ItemLabel,
  SearchableSelectPopover,
  SearchInput,
  SearchWrapper,
  SELECT_SEARCH_THRESHOLD,
  SelectEmptyState,
  SelectItem,
  SelectItemsContainer,
  SelectPopover,
  SelectTrigger,
} from "./selectParts";

const Trigger = styled(SelectTrigger)(() => ({
  lineHeight: "48px",
  padding: "0 32px 0 16px",
}));

const Popover = styled(SelectPopover)(() => ({
  minWidth: "200px",
}));

const SearchPopover = styled(SearchableSelectPopover)(() => ({
  minWidth: "200px",
}));

export interface MultiSelectOption {
  key: string;
  label: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  selected: string[];
  disabled?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  noResultsText?: string;
  onChange: (selected: string[]) => void;
}

export const MultiSelect = ({
  options,
  selected,
  disabled,
  placeholder = "—",
  searchPlaceholder,
  noResultsText,
  onChange,
}: MultiSelectProps) => {
  const { isOpen, position, triggerRef, popoverRef, toggle } = usePopover();
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const isSearchable = options.length > SELECT_SEARCH_THRESHOLD;

  const displayText = useMemo(() => {
    if (selected.length === 0) return placeholder;

    return selected.map((key) => options.find((o) => o.key === key)?.label ?? key).join(", ");
  }, [selected, options, placeholder]);

  const filteredOptions = useMemo(() => {
    if (!isSearchable || !search) return options;

    const query = search.toLowerCase();

    return options.filter((o) => o.label.toLowerCase().includes(query));
  }, [options, search, isSearchable]);

  const handleToggle = (key: string) => {
    const next = selectedSet.has(key) ? selected.filter((k) => k !== key) : [...selected, key];
    onChange(next);
  };

  useEffect(() => {
    if (!isOpen) setSearch("");
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && isSearchable) {
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [isOpen, isSearchable]);

  const items = filteredOptions.map((option) => {
    const isSelected = selectedSet.has(option.key);

    return (
      <SelectItem key={option.key} onClick={() => handleToggle(option.key)}>
        <ItemLabel>{option.label}</ItemLabel>
        {isSelected && <CheckMark />}
      </SelectItem>
    );
  });

  return (
    <>
      <Trigger ref={triggerRef} type="button" disabled={disabled} onClick={toggle}>
        {displayText}
      </Trigger>
      {isOpen &&
        createPortal(
          isSearchable ? (
            <SearchPopover ref={popoverRef} style={position}>
              <SearchWrapper>
                <SearchInput
                  ref={searchRef}
                  type="text"
                  placeholder={searchPlaceholder}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </SearchWrapper>
              <SelectItemsContainer>
                {filteredOptions.length === 0
                  ? noResultsText && <SelectEmptyState>{noResultsText}</SelectEmptyState>
                  : items}
              </SelectItemsContainer>
            </SearchPopover>
          ) : (
            <Popover ref={popoverRef} style={position}>
              {items}
            </Popover>
          ),
          document.body,
        )}
    </>
  );
};
